import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Chargement, Erreur, Infobulle, Modale, Tableau, Vide, type Colonne } from '../components/Elements';
import { formaterDate, formaterDateHeure, formaterMontant } from '../lib/format';
import { telechargerBordereau } from '../lib/documents';
import { IconePdf } from '../components/IconePdf';
import type { Bordereau, Reglement, Remise } from '../types';

/**
 * Remises de cheques (M6, RG-17 a RG-20).
 *
 * Le geste reel : on empile les cheques recus, on les depose en banque, on
 * imprime un bordereau. L'ecran suit cet ordre — constituer, verifier,
 * deposer. Le depot VERROUILLE (RG-19) : c'est tout son interet, et c'est
 * pourquoi il demande confirmation.
 */
export function RemisesPage({ anneeId, cle }: { anneeId: string; cle: string }) {
  const { peut } = useAuth();
  // Une annee cloturee fige ses remises (RG-27, 2026-10-01) : l'ecran
  // cesse de proposer ce que le serveur refusera.
  const modifiable = useAnnee().modifiable;
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);

  // Les remises suivent l'annee du bandeau : elles sont rattachees par
  // les cheques qu'elles contiennent, `remise` n'ayant pas d'annee.
  const remises = useRessource(
    () => api.get<Remise[]>('/api/remises', { anneeId }),
    [cle, anneeId],
  );

  if (remises.chargement) return <Chargement />;
  if (remises.erreur) return <Erreur message={remises.erreur} onReessayer={remises.recharger} />;

  const liste = remises.donnees ?? [];

  /**
   * Supprimer une remise EN PREPARATION. C'est le seul recours pour
   * corriger sa composition, celle-ci se decidant a la creation : on
   * supprime et on refait. Ses cheques retournent au vivier — ils
   * redeviennent « prevus », pas effaces.
   *
   * Le serveur refuse au-dela de la preparation (`remiseModifiable`) ;
   * l'ecran ne fait que ne pas le proposer.
   */
  const supprimer = async (remise: Remise) => {
    if (
      !window.confirm(
        `Supprimer la remise ${remise.reference} ?

` +
          `Ses ${remise.nb_cheques} chèque(s) redeviendront « prévus » et pourront entrer dans ` +
          `une autre remise. La remise, elle, sera effacée.`,
      )
    ) {
      return;
    }
    try {
      await api.delete(`/api/remises/${remise.id}`);
      remises.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.');
    }
  };

  const colonnes: Colonne<Remise>[] = [
    {
      cle: 'reference',
      entete: 'Référence',
      rendu: (r) => <span className="tabulaire font-medium">{r.reference}</span>,
    },
    { cle: 'date', entete: 'Dépôt', rendu: (r) => formaterDate(r.date_depot) },
    {
      cle: 'compte',
      entete: 'Compte',
      rendu: (r) => <span className="text-xs text-encre-500">{r.compte_libelle ?? '—'}</span>,
    },
    {
      cle: 'cheques',
      entete: 'Chèques',
      classe: 'text-right',
      rendu: (r) => <span className="tabulaire">{r.nb_cheques}</span>,
    },
    {
      cle: 'montant',
      entete: 'Montant',
      classe: 'text-right',
      rendu: (r) => <span className="tabulaire font-medium">{formaterMontant(r.montant)}</span>,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (r) =>
        r.statut === 'deposee' ? (
          <Badge ton="neutre">déposée</Badge>
        ) : r.statut === 'validee' ? (
          <Badge ton="succes">validée</Badge>
        ) : (
          <Badge ton="alerte">en préparation</Badge>
        ),
    },
    {
      cle: 'bordereau',
      entete: '',
      classe: 'text-center',
      // Avant le depot : le bordereau de controle, telecharge. Deposee :
      // le document se consulte depuis le Drive — l'icone ouvre le lien,
      // ou dit que l'archivage attend. La ligne entiere ouvre le detail,
      // le clic sur l'icone ne doit pas l'ouvrir au passage.
      rendu: (r) =>
        r.statut === 'deposee' ? (
          r.drive_url ? (
            <a
              href={r.drive_url}
              target="_blank"
              rel="noreferrer"
              className="inline-block rounded p-1 hover:bg-encre-100"
              onClick={(evenement) => evenement.stopPropagation()}
            >
              <IconePdf libelle={`Ouvrir le bordereau de la remise ${r.reference} dans le Drive`} />
            </a>
          ) : (
            <span className="inline-block p-1 opacity-40">
              <IconePdf libelle={`Bordereau de la remise ${r.reference} en attente d’archivage sur le Drive`} />
            </span>
          )
        ) : (
          <button
            type="button"
            className="rounded p-1 hover:bg-encre-100"
            onClick={(evenement) => {
              evenement.stopPropagation();
              void telechargerBordereau(r.id, r.reference).catch((cause: unknown) =>
                window.alert(cause instanceof ErreurApi ? cause.message : 'Téléchargement impossible.'),
              );
            }}
          >
            <IconePdf libelle={`Bordereau de contrôle de la remise ${r.reference} (PDF)`} />
          </button>
        ),
    },
    ...(peut('remise.creer')
      ? [
          {
            cle: 'actions',
            entete: '',
            classe: 'text-right',
            // Seule une remise EN PREPARATION se supprime : validee, sa
            // composition est arretee ; deposee, le bordereau est parti.
            // La ligne entiere ouvre le detail — le clic sur la croix ne
            // doit pas l'ouvrir au passage.
            rendu: (r: Remise) =>
              r.statut === 'preparation' && modifiable ? (
                <Infobulle texte="Supprimer cette remise" className="inline-flex">
                  <button
                    type="button"
                    className="rounded px-1 text-encre-400 hover:bg-red-50 hover:text-red-700"
                    aria-label={`Supprimer la remise ${r.reference}`}
                    onClick={(evenement) => {
                      evenement.stopPropagation();
                      void supprimer(r);
                    }}
                  >
                    ✕
                  </button>
                </Infobulle>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-encre-500">
          Une remise regroupe des chèques déposés le même jour, sur le compte de son entité. Une
          fois déposée, elle est <strong>verrouillée</strong> — le bordereau est parti en banque.
        </p>
        {peut('remise.creer') && modifiable && (
          <button type="button" className="bouton-principal" onClick={() => setCreation(true)}>
            Nouvelle remise
          </button>
        )}
      </div>

      <div className="carte">
        <Tableau
          colonnes={colonnes}
          lignes={liste}
          cleDe={(r) => r.id}
          messageVide="Aucune remise. Constituez-en une à partir des chèques non remis."
          onLigneCliquee={(r) => setOuverte(r.id)}
        />
      </div>

      <FormulaireRemise
        ouverte={creation}
        anneeId={anneeId}
        onFermer={() => setCreation(false)}
        onEnregistre={(id) => {
          setCreation(false);
          remises.recharger();
          setOuverte(id);
        }}
      />

      {ouverte && (
        <DetailRemise
          remiseId={ouverte}
          onFermer={() => setOuverte(null)}
          onChange={remises.recharger}
        />
      )}
    </>
  );
}

/**
 * Composer une remise : une PLAGE de dates d'encaissement, la liste des
 * cheques qui y repondent, et la remise nait avec eux.
 *
 * La composition se fait ICI et nulle part ailleurs (decision du
 * 2026-08-28) : l'ecran de detail ne permet plus d'ajouter ni de retirer
 * un cheque a l'unite. Pour corriger, on supprime la remise tant qu'elle
 * est en preparation et on la refait.
 */
function FormulaireRemise({
  ouverte,
  anneeId,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  anneeId: string;
  onFermer: () => void;
  onEnregistre: (id: string) => void;
}) {
  // Le jour a Paris, pas en UTC : passe minuit d'ete, `toISOString` rend
  // la veille.
  const aujourdhui = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(
    new Date(),
  );
  // Par defaut, le mois en cours : c'est le rythme des remises.
  const premierDuMois = `${aujourdhui.slice(0, 7)}-01`;
  // Le dernier jour se compose a la main. `new Date(a, m, 0).toISOString()`
  // repassait par UTC et rendait le 29 pour un mois de 30 jours : un
  // cheque encaisse le dernier du mois sortait de la plage par defaut.
  const dernierDuMois = (() => {
    const an = Number(aujourdhui.slice(0, 4));
    const mois = Number(aujourdhui.slice(5, 7));
    const jour = new Date(Date.UTC(an, mois, 0)).getUTCDate();
    return `${aujourdhui.slice(0, 7)}-${String(jour).padStart(2, '0')}`;
  })();

  const [du, setDu] = useState(premierDuMois);
  const [au, setAu] = useState(dernierDuMois);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // RG-17 : seuls des cheques, et seulement ceux qu'aucune remise ne
  // porte deja. Le serveur filtre aussi — proposer l'impossible est pire
  // que de le refuser.
  const candidats = useRessource(
    () =>
      ouverte
        ? api.get<Reglement[]>('/api/reglements', {
            anneeId,
            sansRemise: true,
            du,
            au,
            limite: 500,
          })
        : Promise.resolve([]),
    [ouverte, anneeId, du, au],
  );

  const liste = candidats.donnees ?? [];

  /**
   * TOUT EST COCHE des que la liste arrive : on compose une remise avec
   * ce qu'on a recu, on en retire l'exception. L'utilisateur decoche
   * ensuite ce qu'il veut, et rien ne le recoche derriere lui — seule
   * une nouvelle liste repose une selection.
   *
   * LE DECLENCHEUR EST LA LISTE CHARGEE, pas la plage demandee. C'est
   * tout le defaut corrige le 2026-09-02 : `useRessource` ne vide pas
   * `donnees` pendant un rechargement, si bien que le garde se
   * declenchait sur la liste PRECEDENTE — celle de la plage d'avant, ou
   * le tableau vide de la modale fermee — puis considerait la plage
   * traitee. Les vraies lignes arrivaient apres, et rien n'etait coche.
   */
  const signature = liste.map((r) => r.id).join(',');
  const aCocher = useRef(liste);
  aCocher.current = liste;
  const [selectionPosee, setSelectionPosee] = useState<string | null>(null);
  useEffect(() => {
    if (!ouverte) {
      setSelectionPosee(null);
      return;
    }
    if (candidats.chargement || selectionPosee === signature) return;
    setSelectionPosee(signature);
    setSelection(new Set(aCocher.current.map((r) => r.id)));
  }, [ouverte, candidats.chargement, signature, selectionPosee]);

  const total = liste
    .filter((r) => selection.has(r.id))
    .reduce((somme, r) => somme + r.montant_net, 0);

  const basculer = (id: string) => {
    const suivant = new Set(selection);
    if (suivant.has(id)) suivant.delete(id);
    else suivant.add(id);
    setSelection(suivant);
  };

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setErreur(null);

    if (selection.size === 0) {
      setErreur('Choisissez au moins un chèque : une remise vide n’a pas d’objet.');
      return;
    }

    setEnCours(true);
    try {
      const remise = await api.post<Remise>('/api/remises', {
        // L'annee du bandeau entre dans la reference (RG-18) : une remise
        // se compose toujours dans l'annee que l'ecran affiche.
        anneeId,
        reglementIds: [...selection],
      });
      onEnregistre(remise.id);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Nouvelle remise de chèques" ouverte={ouverte} onFermer={onFermer} large>
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="etiquette">Encaissement du</span>
            <input
              className="champ"
              type="date"
              value={du}
              onChange={(e) => setDu(e.target.value)}
              required
              autoFocus
            />
          </label>
          <label className="block">
            <span className="etiquette">au</span>
            <input
              className="champ"
              type="date"
              value={au}
              onChange={(e) => setAu(e.target.value)}
              required
            />
          </label>
        </div>

        {/* La date de dépôt n'est pas saisie : au moment où l'on compose,
            on ne sait pas quel jour le bordereau partira. Elle se posera
            au dépôt, et c'est elle qui datera l'encaissement des chèques. */}
        <p className="text-xs text-encre-400">
          Référence : <span className="tabulaire">R{aujourdhui.slice(0, 4)}{aujourdhui.slice(5, 7)}</span>,
          suffixée s’il y a déjà une remise ce mois-ci (RG-18). La date de dépôt sera celle du jour
          où vous déposerez.
        </p>

        {candidats.chargement ? (
          <Chargement />
        ) : liste.length === 0 ? (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-sm text-encre-500">
            Aucun chèque en attente sur cette période.
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-md border border-encre-200">
            <ul className="divide-y divide-encre-100">
              {liste.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selection.has(r.id)}
                    onChange={() => basculer(r.id)}
                  />
                  <span className="tabulaire w-24 text-xs text-encre-500">
                    {formaterDate(r.date_encaissement)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {r.emetteur_nom ?? '—'}
                    {r.numero_cheque && (
                      <span className="tabulaire ml-2 text-xs text-encre-400">
                        n° {r.numero_cheque}
                      </span>
                    )}
                  </span>
                  <span className="tabulaire font-medium">{formaterMontant(r.montant_net)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {liste.length > 0 && (
          <p className="text-sm text-encre-600">
            <strong>{selection.size}</strong> chèque(s) retenu(s) — {formaterMontant(total)}
          </p>
        )}

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            {enCours ? 'Création…' : 'Créer la remise'}
          </button>
        </div>
      </form>
    </Modale>
  );
}

function DetailRemise({
  remiseId,
  onFermer,
  onChange,
}: {
  remiseId: string;
  onFermer: () => void;
  onChange: () => void;
}) {
  const { peut } = useAuth();
  // Annee cloturee : ni validation, ni depot, ni composition (RG-27).
  const modifiable = useAnnee().modifiable;
  const [enCours, setEnCours] = useState(false);
  const [telechargement, setTelechargement] = useState(false);

  const bordereau = useRessource(
    () => api.get<Bordereau>(`/api/remises/${remiseId}/bordereau`),
    [remiseId],
  );

  if (bordereau.chargement) return <Modale titre="Remise" ouverte onFermer={onFermer}><Chargement /></Modale>;
  if (!bordereau.donnees) return null;

  const { remise, lignes, document } = bordereau.donnees;
  // Une remise VALIDEE fige deja sa composition ; le depot fige tout.
  // L'ajout et le retrait a l'unite ont disparu (2026-08-28) : une remise
  // se compose a sa creation, par la plage de dates d'encaissement.
  const enPreparation = remise.statut === 'preparation';

  const valider = async () => {
    if (
      !window.confirm(
        `Valider la remise ${remise.reference} (${remise.nb_cheques} chèques, ` +
          `${formaterMontant(remise.montant)}) ?

` +
          `Sa composition sera ARRÊTÉE : ses chèques ne se modifieront plus. ` +
          `Ils resteront « en cours » — c’est le dépôt qui les encaissera.`,
      )
    ) {
      return;
    }
    setEnCours(true);
    try {
      await api.post(`/api/remises/${remiseId}/validation`, {});
      bordereau.recharger();
      onChange();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Validation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const deposer = async () => {
    if (
      !window.confirm(
        `Déposer la remise ${remise.reference} (${remise.nb_cheques} chèques, ` +
          `${formaterMontant(remise.montant)}) ?\n\n` +
          `Elle sera VERROUILLÉE : ni ajout, ni retrait, ni modification des chèques ` +
          `qu’elle contient. Cette action ne s’annule pas.`,
      )
    ) {
      return;
    }
    setEnCours(true);
    try {
      await api.post(`/api/remises/${remiseId}/depot`, {});
      bordereau.recharger();
      onChange();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Dépôt impossible.');
    } finally {
      setEnCours(false);
    }
  };

  // Une remise deposee sans document conserve — reprise du classeur, ou
  // production echouee au depot : on le produit a la demande.
  const produireDocument = async () => {
    setEnCours(true);
    try {
      await api.post(`/api/remises/${remiseId}/document`, {});
      bordereau.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Production impossible.');
    } finally {
      setEnCours(false);
    }
  };

  // Archiver sur le Drive sans attendre le passage quotidien : une
  // action sortante, sous sa propre permission.
  const archiver = async () => {
    if (!document) return;
    setEnCours(true);
    try {
      await api.post(`/api/documents/${document.id}/archiver`, {});
      bordereau.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Archivage impossible.');
    } finally {
      setEnCours(false);
    }
  };

  // LE BORDEREAU EST UN PDF PRODUIT PAR LE SERVEUR (ADR-0014, 2026-10-03).
  // Le calque d'impression et son `window.print()` ont disparu avec les
  // trois defauts du 2026-09-03 qu'ils portaient. Avant le depot, le
  // serveur rend un projet, marque en filigrane.
  const telecharger = async () => {
    setTelechargement(true);
    try {
      await telechargerBordereau(remiseId, remise.reference);
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Téléchargement impossible.');
    } finally {
      setTelechargement(false);
    }
  };

  return (
    <Modale titre={`Remise ${remise.reference}`} ouverte onFermer={onFermer}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span>
            Dépôt du <strong>{formaterDate(remise.date_depot)}</strong>
          </span>
          <span className="tabulaire">
            {remise.nb_cheques} chèque(s) — {formaterMontant(remise.montant)}
          </span>
          {remise.statut === 'deposee' ? (
            <Badge ton="neutre">déposée</Badge>
          ) : remise.statut === 'validee' ? (
            <Badge ton="succes">validée</Badge>
          ) : (
            <Badge ton="alerte">en préparation</Badge>
          )}
        </div>

        {remise.statut === 'validee' && (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-600">
            Cette remise est <strong>validée</strong> : sa composition est arrêtée et ses chèques
            ne se modifient plus. Ils restent « en cours » — rien n’est encore parti en banque.
            Le dépôt les encaissera.
          </p>
        )}

        {remise.statut === 'deposee' && (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-600">
            Cette remise est déposée, donc <strong>verrouillée</strong> (RG-19). Le bordereau est
            parti en banque : une correction passe désormais par un incident sur le chèque
            concerné, pas par une modification de la remise.
          </p>
        )}

        {/* LE DOCUMENT CONSERVE (ADR-0014) : produit au depot, une seule
            fois. Le bloc dit s'il existe, depuis quand, par qui — et, a
            l'etape 3, s'il est parti sur le Drive. */}
        {remise.statut === 'deposee' && (
          <div className="rounded-md border border-encre-200 p-3 text-sm">
            <div className="etiquette">Document</div>
            {document ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{document.nature ?? 'Bordereau de remise'}</span>
                {document.archivage === 'archive' ? (
                  <Badge ton="succes">archivé sur le Drive</Badge>
                ) : (
                  <Badge ton="alerte">en attente d’archivage</Badge>
                )}
                <span className="text-xs text-encre-500">
                  produit le {formaterDateHeure(document.genere_le)}
                  {document.genere_par_nom ? ` par ${document.genere_par_nom}` : ''}
                  {document.drive_chemin && (
                    <>
                      {' · '}
                      <span className="tabulaire">{document.drive_chemin}</span>
                    </>
                  )}
                </span>
                {document.drive_url && (
                  <a
                    href={document.drive_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs underline"
                  >
                    Ouvrir dans le Drive ↗
                  </a>
                )}
                {document.archivage !== 'archive' && peut('document.archiver') && (
                  <button type="button" className="text-xs underline" disabled={enCours} onClick={() => void archiver()}>
                    Archiver maintenant
                  </button>
                )}
                {document.archivage === 'echec' && document.erreur && (
                  <span className="basis-full text-xs text-red-700">Dernier essai : {document.erreur}</span>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>Bordereau de remise</span>
                <Badge ton="alerte">pas encore produit</Badge>
                <span className="text-xs text-encre-500">
                  remise déposée avant les documents, ou production échouée au dépôt
                </span>
                {peut('remise.deposer') && (
                  <button
                    type="button"
                    className="text-xs underline"
                    disabled={enCours}
                    onClick={() => void produireDocument()}
                  >
                    Produire et archiver
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {lignes.length === 0 ? (
          <Vide message="Aucun chèque dans cette remise." />
        ) : (
          <ul className="divide-y divide-encre-100 rounded-md border border-encre-200">
            {lignes.map((l) => (
              <li key={l.reglement_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{l.emetteur_nom ?? '—'}</span>
                  <span className="ml-2 tabulaire text-xs text-encre-500">
                    n° {l.numero_cheque} · {l.banque}
                  </span>
                </span>
                <span className="tabulaire">{formaterMontant(l.montant)}</span>
              </li>
            ))}
          </ul>
        )}

        {enPreparation && (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
            La composition d’une remise se décide à sa création, par la plage de dates
            d’encaissement. Pour la corriger, supprimez cette remise et recréez-la — ses chèques
            redeviendront « prévus ».
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Fermer
          </button>
          {/* Le bordereau de CONTROLE, avant le depot seulement : deposee,
              la remise se consulte depuis le Drive (bloc Document). */}
          {lignes.length > 0 && remise.statut !== 'deposee' && (
            <button
              type="button"
              className="bouton-secondaire"
              disabled={telechargement}
              onClick={() => void telecharger()}
            >
              {telechargement ? 'Bordereau…' : 'Bordereau (PDF)'}
            </button>
          )}
          {enPreparation && modifiable && peut('remise.creer') && lignes.length > 0 && (
            <button
              type="button"
              className="bouton-principal"
              disabled={enCours}
              onClick={() => void valider()}
            >
              Valider
            </button>
          )}
          {remise.statut === 'validee' && modifiable && peut('remise.deposer') && (
            <button
              type="button"
              className="bouton-principal"
              disabled={enCours}
              onClick={() => void deposer()}
            >
              Déposer
            </button>
          )}
        </div>
      </div>
    </Modale>
  );
}
